import axios from 'axios';
import { toast } from 'react-hot-toast';
import { tooManyRequestsMessageFromAxiosError } from './httpRateLimit';

let last429ToastAt = 0;
const TOAST429_THROTTLE_MS = 3000;

// Endpoints where a 401 is an expected, locally-handled outcome, NOT a
// mid-session expiry: login/OTP failures (wrong password), the session probe on
// load (logged-out == 401), and a failed token refresh. These must not trigger
// the global redirect below, otherwise the logged-out session probe loops
// login <-> dashboard forever.
const AUTH_ENDPOINTS = [
  '/admin/login',
  '/admin/auth/verify-otp',
  '/admin/auth/refresh-token',
  '/admin/get-admin-user',
];
const isAuthRequest = (url = '') => AUTH_ENDPOINTS.some((path) => url.includes(path));

export const setupAxiosInterceptors = () => {
  // Admin auth now rides on the httpOnly `adminAccessToken` cookie (CHO-1473).
  // Attach credentials to every backend call so the cookie is sent (and the
  // Set-Cookie from login/OTP is stored). Scoped to our backend only, so we
  // never leak cookies to third-party hosts (e.g. S3 uploads) or trip their CORS.
  axios.interceptors.request.use((config) => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (
      backend
      && typeof config.url === 'string'
      && config.url.startsWith(backend)
    ) {
      config.withCredentials = true;

      // Transitional: sessions that predate the cookie migration still hold a
      // token in localStorage. Keep sending it as a bearer fallback until it
      // expires and everyone re-logs in on the cookie. Remove in a cleanup PR.
      const legacyToken = window.localStorage.getItem('accessToken');
      if (legacyToken && !config.headers?.Authorization) {
        config.headers = { ...config.headers, Authorization: legacyToken };
      }
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 429) {
        const now = Date.now();
        if (now - last429ToastAt >= TOAST429_THROTTLE_MS) {
          last429ToastAt = now;
          const msg = tooManyRequestsMessageFromAxiosError(error);
          try {
            toast.error(msg);
          } catch (_e) {
            console.warn(msg);
          }
        }
      }

      if (error.response?.status === 401 && !isAuthRequest(error.config?.url)) {
        // Session expired on an authenticated request: clear token and redirect.
        window.localStorage.removeItem('accessToken');
        window.location.href = '/dashboard';
      }
      return Promise.reject(error);
    }
  );
};
